import { FC, useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { SelectComponentsProps } from 'react-select/src/Select';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ISelectProps } from '../form/types';
import ValueContainer from '../form/MultiSelectValueContainer';

interface IMultiSelectProps extends ISelectProps {
  onChange: (selected: any) => void;
  className?: string;
  showTotal?: boolean;
  limitOptions?: boolean;
  allowSelectAll?: boolean;
  ariaLabel?: string;
}

/*
 * Abstracted select component through `react-select`.
 * Used when multi select.
 */
const MultiSelectFilter: FC<IMultiSelectProps> = ({
  placeholder,
  isClearable,
  isDisabled,
  isSearchable,
  options,
  noOptionsMessage,
  disableOptions,
  props,
  onChange,
  className = '',
  showTotal,
  value,
  limitOptions,
  disableFilterOption,
  allowSelectAll,
  ariaLabel,
}) => {
  const { t } = useTranslation();

  // Search term state
  const [searchTerm, setSearchTerm] = useState('');

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  // Reset search term when options change
  useEffect(() => {
    setSearchTerm('');
  }, [options]);

  const toggleSelectAll = () => {
    const allSelected = isAllSelected;
    onChange(allSelected ? [] : options);
    setIsAllSelected(!allSelected);
  };

  // Customize option
  const Option = (optionProps: any) => {
    const { label, isSelected } = optionProps;
    return (
      <div>
        <components.Option {...optionProps}>
          <label
            htmlFor={label}
            className="mb-1 mt-1"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {label}
          </label>
          <Form.Check
            custom
            type="checkbox"
            name={label}
            id={label}
            value={label}
            checked={isSelected}
          />
        </components.Option>
      </div>
    );
  };

  const OptionsContainer = (menuListProps: any) => {
    return (
      <>
        <components.MenuList {...menuListProps}>{menuListProps.children}</components.MenuList>
        {allowSelectAll && (options?.length || 0) > 0 && (
          <OptionButton onClick={toggleSelectAll}>
            <SelectAllBtn>{isAllSelected ? t('form.removeAll') : t('form.selectAll')}</SelectAllBtn>
          </OptionButton>
        )}
      </>
    );
  };

  // Customize multi value
  const MultiValue = (multiValueProps: any) => {
    const { data } = multiValueProps;
    return (
      <components.MultiValue {...multiValueProps}>
        <span>{data.label}</span>
      </components.MultiValue>
    );
  };

  // Format of group labels, which wrap options
  const formatGroupLabel = (data: any) => (
    <Group>
      <GroupLabel>{data.label}</GroupLabel>
      <GroupBadge>{data.options.length}</GroupBadge>
    </Group>
  );

  // Render options and add `allOption` if needed
  const filterOption = (option: any) => {
    if (!searchTerm) {
      return true;
    }

    return (
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (option.label_en && option.label_en.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Render options and add `allOption` if needed
  const renderOptions = () => {
    let selectOptions = options?.slice();

    const limit = process.env.REACT_APP_OPTION_LIMIT ? parseInt(process.env.REACT_APP_OPTION_LIMIT, 10) : 100;

    if (limitOptions && selectOptions) {
      selectOptions = selectOptions.filter(option => filterOption(option)).slice(0, limit);
    } else if (selectOptions) {
      selectOptions = selectOptions.filter(option => filterOption(option));
    }

    return selectOptions;
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    return inputValue;
  };

  const handleOnChange = (selected: any, event: any) => {
    let selectedModified = selected;

    if (Array.isArray(selectedModified) && !selectedModified.length) selectedModified = null;

    if (allowSelectAll) {
      const isAllClicked = event.action === 'select-option' && event.option.value === '*';

      if (isAllClicked) {
        setIsAllSelected(!isAllSelected);
      } else if (Array.isArray(selectedModified) && selectedModified.length === options?.length) {
        setIsAllSelected(true);
      } else {
        setIsAllSelected(false);
      }
    }

    return onChange(selectedModified);
  };

  const componentProps: SelectComponentsProps = {
    ...props,
    className: `c-custom-select ${className}`,
    classNamePrefix: 'cs',
    placeholder: placeholder || t('form.select'),
    isDisabled,
    isClearable,
    isSearchable,
    components: { Option, MenuList: OptionsContainer, MultiValue, ValueContainer },
    hideSelectedOptions: false,
    defaultValue: value,
    value,
    options: renderOptions(),
    onInputChange: handleInputChange,
    isOptionDisabled: (option: any) => {
      return disableOptions || option.isDisabled;
    },
    noOptionsMessage: () => noOptionsMessage || t('form.noOptions'),
    isMulti: true,
    closeMenuOnSelect: false,
    showTotal,
    formatGroupLabel,
    onChange: handleOnChange,
    filterOption: null,
    allowSelectAll,
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#32bfa3',
        primary25: '#f2f3f8',
        primary50: '#e8e9ef',
        neutral50: '#ced4da',
      },
    }),
  };

  return ariaLabel ? (
    <LabelWrapper aria-label={ariaLabel}>
      <Select
        {...componentProps}
        aria-label={ariaLabel}
      />
    </LabelWrapper>
  ) : (
    <Select
      {...componentProps}
      aria-label={ariaLabel}
    />
  );
};

const LabelWrapper = styled.div`
  display: contents;
`;

const OptionButton = styled.div`
  color: ${props => props.theme.colors.primary};
  border-top: ${props => `1px solid ${props.theme.colors.gray}`};
  cursor: pointer;

  .cs__option {
    justify-content: center !important;
  }
`;

const SelectAllBtn = styled.div`
  padding: 0.75rem 1rem;
  text-align: center;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.colors.dark};
`;

const GroupLabel = styled.span`
  font-weight: 800;
`;

const GroupBadge = styled.span`
  background: ${props => props.theme.colors.lighter};
  border-radius: 2em;
  display: inline-block;
  padding: 0.325rem;
  text-align: center;
`;

export default MultiSelectFilter;
